import React from 'react';
import { Paper, Group, Title, Text, Stack, Button, Grid } from '@mantine/core';
import { IconHome, IconUsers, IconTools, IconCreditCard, IconWorld } from '@tabler/icons-react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import StudioManagement from './StudioManagement';
import StaffManagement from './StaffManagementSettings';
import ServicesManagement from './ServicesManagementSettings';
import PaymentSettings from './PaymentSettings';
import WebsiteSettings from './WebsiteSettings';
import './SettingsPage.css';
import { useMediaQuery } from '@mantine/hooks';
import { useSalon } from '../../context/SalonContext';

function SettingsPage({ 
  onBusinessHoursSubmit,
  onServicesSubmit
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { currentUser, businessInfo } = useSalon();
  
  const menuItems = [
    { 
      id: 'studio', 
      label: 'Studio',
      icon: <IconHome size="1.2rem" />,
      path: '/admin/settings/studio',
      element: <StudioManagement onSubmit={onBusinessHoursSubmit} />
    },
    { 
      id: 'staff', 
      label: 'Staff',
      icon: <IconUsers size="1.2rem" />,
      path: '/admin/settings/staff',
      element: <StaffManagement />
    },
    { 
      id: 'services', 
      label: 'Services',
      icon: <IconTools size="1.2rem" />,
      path: '/admin/settings/services',
      element: <ServicesManagement onSubmit={onServicesSubmit} />
    },
    { 
      id: 'website', 
      label: 'Website',
      icon: <IconWorld size="1.2rem" />,
      path: '/admin/settings/website',
      element: <WebsiteSettings />,
      hidden: currentUser?.role !== 'Owner' || businessInfo?.website_url
    },
    { 
      id: 'payments', 
      label: 'Payments',
      icon: <IconCreditCard size="1.2rem" />,
      path: '/admin/settings/payments',
      element: <PaymentSettings />,
      hidden: currentUser?.role !== 'Owner'
    }
  ];

  const isSelected = (path) => location.pathname === path;

  return (
    <Stack size="xl" p={{ base: 'sm', xs: 'md', sm: 'xl' }} gap="xs" className="settings-page">
      <Group position="apart" mb={{ base: 'xs', xs: 'sm' }}>
        <div>
          <Title order={2}>Settings</Title>
          <Text size="sm" c="dimmed">
            Manage your studio settings, staff, and services
          </Text>
        </div>
      </Group>

      { !isMobile && <Grid>
          <Grid.Col span={3}>
            <Paper className="menu-list" p="xs" withBorder>
              <Stack spacing="xs">
                {menuItems.map(item => (
                  !item.hidden && (
                    <Button
                      key={item.id}
                      variant="subtle"
                      className={isSelected(item.path) ? "" : "clickable"}
                      bg={isSelected(item.path) ? "var(--mantine-color-violet-6)" : "var(--mantine-color-white)"}
                      onClick={() => navigate(item.path)}
                      fullWidth
                      styles={{
                        label: {
                          flex: 1,
                          textAlign: 'left'
                        }
                      }}
                    >
                      <Text c={isSelected(item.path) ? "white" : "#333"} size="md">{item.icon}</Text>
                      <Text ml="xs" size="sm" c={isSelected(item.path) ? "white" : "#333"} fw={500}>{item.label}</Text>
                    </Button>
                  )
                ))}
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={9}>
            <Paper p="xs" withBorder>
              <Routes>
                {menuItems.map(item => (
                  !item.hidden && (
                    <Route key={item.id} path={item.id} element={item.element} />
                  )
                ))}
                <Route path="*" element={<StudioManagement onSubmit={onBusinessHoursSubmit} />} />
              </Routes>
            </Paper>
          </Grid.Col>
        </Grid>
      }

      { isMobile && <Stack gap={0}>
        <Paper p="xs" w="100%" h="fit-content" withBorder>
          <Group gap="xs" justify="space-around" w="100%" style={{maxWidth: '400px'}} mx="auto">
            {menuItems.map(item => (
              !item.hidden && (
                <Button
                  key={item.id}
                  variant="subtle"
                  className={isSelected(item.path) ? "" : "clickable"}
                  bg={isSelected(item.path) ? "var(--mantine-color-violet-6)" : "var(--mantine-color-white)"}
                  onClick={() => navigate(item.path)}
                  h="fit-content"
                  p="xs"
                >
                  <Text c={isSelected(item.path) ? "white" : "#333"} size="xs">{item.icon}</Text>
                  <Text ml="xs" size="xs" c={isSelected(item.path) ? "white" : "#333"} fw={500}>{item.label}</Text>
                </Button>
              )
            ))}
          </Group>
        </Paper>
        <Routes>
          {menuItems.map(item => (
            !item.hidden && (
              <Route key={item.id} path={item.id} element={item.element} />
            )
          ))}
          <Route path="*" element={<StudioManagement onSubmit={onBusinessHoursSubmit} />} />
        </Routes>
      </Stack> }
    </Stack>
  );
}

export default SettingsPage; 